<script setup>
  import { computed, inject, ref } from "vue";
  import { Trash2 } from "lucide-vue-next";

  import { Button } from "@/components/ui/button";
  import { ConfirmDialog } from "@/components/episto-ui/confirm-dialog";
  import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

  import { useSurveyTargetsStore } from "@stores/survey/surveyTargets";

  const props = defineProps({
    card: { type: Object, required: true },
    cardToken: { type: String, required: true },
    disabled: { type: Boolean, default: false },
    compact: { type: Boolean, default: false },
  });

  const buttonClass = computed(() => (props.compact ? "size-6 text-muted-foreground" : ""));
  const iconClass = computed(() => (props.compact ? "size-3.5" : "h-4 w-4"));

  const emit = defineEmits(["confirm"]);
  const eventBus = inject("editEventBus");

  const popoverOpen = ref(false);

  const surveyTargetsStore = useSurveyTargetsStore();
  const { isCardBindedToTarget } = surveyTargetsStore;

  const hidePopup = () => (popoverOpen.value = false);
  const showPopup = () => (popoverOpen.value = true);
  const handleCommand = (command, token) => eventBus.emit({ command, token });
  const handleConfirmDelete = () => {
    handleCommand("remove", props.cardToken);
    emit("confirm");
  };
</script>

<template>
  <!-- Popover for bound to target cards -->
  <Popover
    v-if="isCardBindedToTarget(card.token)"
    v-model:open="popoverOpen">
    <PopoverTrigger as-child>
      <Button
        variant="ghost"
        size="icon-sm"
        :class="buttonClass"
        :disabled="disabled"
        @click="showPopup">
        <Trash2 :class="iconClass" />
      </Button>
    </PopoverTrigger>
    <PopoverContent class="w-64">
      <div class="space-y-3">
        <h4 class="font-medium text-13">This card can't be deleted</h4>
        <p class="text-13 text-muted-foreground">You have to change the target if you want to proceed</p>
        <div class="flex justify-end">
          <Button
            size="sm"
            @click="hidePopup">
            Ok
          </Button>
        </div>
      </div>
    </PopoverContent>
  </Popover>

  <ConfirmDialog
    v-else
    title="Delete card"
    description="Are you sure you want to delete this card? This change cannot be undone."
    confirm-label="Yes, delete"
    cancel-label="No, cancel"
    @confirm="handleConfirmDelete">
    <template #trigger>
      <Button
        variant="ghost"
        size="icon-sm"
        :class="buttonClass"
        :disabled="disabled">
        <Trash2 :class="iconClass" />
      </Button>
    </template>
  </ConfirmDialog>
</template>
